import type { I18n, MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { formValidationMessages } from "@offstreetparking/domain/i18n";
import type { $ZodErrorMap, $ZodIssue } from "zod/v4/core";
import en from "zod/v4/locales/en.js";
import { formatCustomIssueValues, formatDateBound } from "./format-zod-error-values";

type MessageValues = Record<string, unknown>;

type IssueWithMeta = $ZodIssue & {
	inst?: { _zod?: { def?: { meta?: Record<string, unknown> } } };
	params?: Record<string, unknown> & {
		field?: unknown;
		i18n?: unknown;
		label?: unknown;
	};
};

const defaultLocaleErrorMap = en().localeError;

const defaultFieldLabel = msg({
	message: "This field",
	comment:
		"Stands in for the field name in a validation error when the schema supplies no label — slots into '{field} is required', '{field} is invalid', etc.",
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const isDescriptor = (value: unknown): value is MessageDescriptor =>
	isRecord(value) && typeof value.id === "string" && typeof value.message === "string";

const flattenDescriptors = (
	value: Record<string, unknown>,
	prefix = "",
	result: Record<string, MessageDescriptor> = {},
) => {
	for (const [key, item] of Object.entries(value)) {
		const path = prefix ? `${prefix}.${key}` : key;
		if (isDescriptor(item)) {
			result[path] = item;
		} else if (isRecord(item)) {
			flattenDescriptors(item, path, result);
		}
	}
	return result;
};

const MESSAGE_BY_KEY = flattenDescriptors({
	errors: formValidationMessages.errors,
	types: formValidationMessages.types,
	validations: formValidationMessages.validations,
});

const render = (i18n: I18n, descriptor: MessageDescriptor, values?: MessageValues): string =>
	i18n._(descriptor.id, values, { message: descriptor.message });

const jsonStringifyReplacer = (_: string, value: unknown): unknown => {
	if (typeof value === "bigint") {
		return value.toString();
	}
	return value;
};

const joinValues = <T extends unknown[]>(array: T, separator = " | "): string =>
	array.map((val) => (typeof val === "string" ? `'${val}'` : val)).join(separator);

const titleCase = (segment: string): string =>
	segment
		.split(/[\s_-]+/)
		.flatMap((word) => word.replace(/([a-z])([A-Z])/g, "$1 $2").split(" "))
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");

const lastNonNumericPathSegment = (path: ReadonlyArray<PropertyKey> | undefined): string | undefined => {
	if (!path?.length) {
		return undefined;
	}
	for (let i = path.length - 1; i >= 0; i--) {
		const segment = path[i];
		if (typeof segment === "string") {
			return segment;
		}
	}
	return undefined;
};

const resolveFieldLabel = (i18n: I18n, issue: IssueWithMeta): string => {
	const fromParams = isDescriptor(issue.params?.label)
		? render(i18n, issue.params.label)
		: typeof issue.params?.label === "string"
			? issue.params.label
			: typeof issue.params?.field === "string"
				? issue.params.field
				: isRecord(issue.params?.i18n) &&
						isRecord(issue.params.i18n.values) &&
						typeof issue.params.i18n.values.field === "string"
					? issue.params.i18n.values.field
					: undefined;
	if (fromParams) {
		return fromParams;
	}

	const meta = issue.inst?._zod?.def?.meta;
	if (typeof meta?.label === "string") {
		return meta.label;
	}
	if (typeof meta?.title === "string") {
		return meta.title;
	}

	const lastSegment = lastNonNumericPathSegment(issue.path);
	return lastSegment ? titleCase(lastSegment) : render(i18n, defaultFieldLabel);
};

const parsedType = (data: unknown): string => {
	const t = typeof data;
	switch (t) {
		case "number":
			return Number.isNaN(data) ? "NaN" : "number";
		case "object":
			if (Array.isArray(data)) {
				return "array";
			}
			if (data === null) {
				return "null";
			}
			if (
				Object.getPrototypeOf(data) !== Object.prototype &&
				(data as { constructor?: { name?: string } }).constructor
			) {
				return (data as { constructor: { name: string } }).constructor.name;
			}
			return t;
		default:
			return t;
	}
};

const descriptorForKey = (key: string): MessageDescriptor | undefined =>
	MESSAGE_BY_KEY[key.replace(/^formValidation:/, "")];

const descriptorForCustomKey = (key: string, values: MessageValues): MessageDescriptor | undefined => {
	const normalizedKey = key.replace(/^formValidation:/, "");
	const descriptor = descriptorForKey(normalizedKey);
	if (descriptor) {
		return descriptor;
	}
	if (typeof values.count === "number") {
		return descriptorForKey(`${normalizedKey}_${values.count === 1 ? "one" : "other"}`);
	}
	return undefined;
};

// Zod reports `int`; the catalog has carried the descriptor as `integer` since before zod v4.
const TYPE_KEY_ALIASES: Record<string, string> = { int: "integer" };

const typeLabel = (i18n: I18n, type: string): string => {
	const key = TYPE_KEY_ALIASES[type] ?? type;
	return render(i18n, descriptorForKey(`types.${key}`) ?? { id: type, message: type });
};

const validationLabel = (i18n: I18n, validation: string): string =>
	render(i18n, descriptorForKey(`validations.${validation}`) ?? { id: validation, message: validation });

const getCustomDescriptorAndValues = (
	param: unknown,
	defaultKey: string,
): { descriptor: MessageDescriptor; values: MessageValues; key: string } => {
	if (typeof param === "string") {
		return {
			descriptor: descriptorForCustomKey(param, {}) ?? formValidationMessages.errors.custom,
			values: {},
			key: param,
		};
	}
	if (isDescriptor(param)) {
		return { descriptor: param, values: {}, key: "" };
	}
	if (isRecord(param)) {
		if (isDescriptor(param.i18n)) {
			const values = isRecord(param.values) ? param.values : {};
			return { descriptor: param.i18n, values, key: "" };
		}
		if (isDescriptor(param)) {
			const values = isRecord(param.values) ? param.values : {};
			return { descriptor: param, values, key: "" };
		}
		const key = typeof param.key === "string" ? param.key : defaultKey;
		const values = isRecord(param.values) ? param.values : {};
		return {
			descriptor: descriptorForCustomKey(key, values) ?? formValidationMessages.errors.custom,
			values,
			key,
		};
	}
	return {
		descriptor: descriptorForKey(defaultKey) ?? formValidationMessages.errors.custom,
		values: {},
		key: defaultKey,
	};
};

const messageFromFallback = (issue: Parameters<NonNullable<typeof defaultLocaleErrorMap>>[0]): string => {
	const localeFallback = defaultLocaleErrorMap(issue);
	return (typeof localeFallback === "string" ? localeFallback : localeFallback?.message) ?? "";
};

export const makeLinguiZodErrorMap =
	(i18n: I18n): $ZodErrorMap =>
	(issue) => {
		const issueWithMeta = issue as IssueWithMeta;
		const field = resolveFieldLabel(i18n, issueWithMeta);
		const hasPath = (issue.path?.length ?? 0) > 0;
		const path = issue.path?.filter((segment) => typeof segment === "string").join(".") ?? "";
		const baseValues = { field, path: path || undefined };

		switch (issue.code) {
			case "invalid_type": {
				if (issue.input === undefined) {
					return {
						message: render(
							i18n,
							formValidationMessages.errors.invalid_type_received_undefined,
							baseValues,
						),
					};
				}
				if (issue.input === null) {
					return {
						message: render(i18n, formValidationMessages.errors.invalid_type_received_null, baseValues),
					};
				}
				const parsed = parsedType(issue.input).toLocaleLowerCase();
				return {
					message: render(i18n, formValidationMessages.errors.invalid_type, {
						...baseValues,
						expected: typeLabel(i18n, issue.expected),
						received: typeLabel(i18n, parsed),
					}),
				};
			}
			case "invalid_value": {
				const descriptor =
					issue.values.length === 1
						? formValidationMessages.errors.invalid_value_one
						: formValidationMessages.errors.invalid_value_other;
				return {
					message: render(i18n, descriptor, {
						...baseValues,
						values: joinValues(issue.values, "|"),
						expected: JSON.stringify(issue.values, jsonStringifyReplacer),
					}),
				};
			}
			case "unrecognized_keys": {
				const descriptor =
					issue.keys.length === 1
						? formValidationMessages.errors.unrecognized_keys_one
						: formValidationMessages.errors.unrecognized_keys_other;
				return { message: render(i18n, descriptor, { ...baseValues, keys: joinValues(issue.keys, ", ") }) };
			}
			case "invalid_union":
				return { message: render(i18n, formValidationMessages.errors.invalid_union, baseValues) };
			case "invalid_key": {
				const offendingKey = issue.path?.[issue.path.length - 1] ?? "";
				return {
					message: render(i18n, formValidationMessages.errors.invalid_key_one, {
						...baseValues,
						keys: String(offendingKey),
						origin: issue.origin,
					}),
				};
			}
			case "invalid_element":
				return {
					message: render(i18n, formValidationMessages.errors.invalid_element, {
						...baseValues,
						origin: issue.origin,
					}),
				};
			case "too_small": {
				const minimum = issue.origin === "date" ? new Date(issue.minimum as number) : issue.minimum;
				const bound =
					!issue.exact &&
					issue.inclusive &&
					minimum === 1 &&
					(issue.origin === "array" || issue.origin === "string")
						? "inclusive_one"
						: issue.exact
							? "exact"
							: issue.inclusive
								? "inclusive"
								: "not_inclusive";
				const descriptor = descriptorForKey(`errors.too_small.${issue.origin}.${bound}`);
				const minimumValue = issue.origin === "date" ? formatDateBound(minimum) : minimum;
				return descriptor
					? { message: render(i18n, descriptor, { ...baseValues, minimum: minimumValue }) }
					: { message: messageFromFallback(issue) };
			}
			case "too_big": {
				const maximum = issue.origin === "date" ? new Date(issue.maximum as number) : issue.maximum;
				const bound = issue.exact ? "exact" : issue.inclusive ? "inclusive" : "not_inclusive";
				const key =
					hasPath && issue.origin === "array"
						? `errors.too_big.${issue.origin}.${bound}_with_path`
						: `errors.too_big.${issue.origin}.${bound}`;
				const descriptor = descriptorForKey(key);
				const maximumValue = issue.origin === "date" ? formatDateBound(maximum) : maximum;
				return descriptor
					? { message: render(i18n, descriptor, { ...baseValues, maximum: maximumValue }) }
					: { message: messageFromFallback(issue) };
			}
			case "invalid_format": {
				const key =
					issue.format === "starts_with"
						? "errors.invalid_format.startsWith"
						: issue.format === "ends_with"
							? "errors.invalid_format.endsWith"
							: issue.format === "includes"
								? "errors.invalid_format.includes"
								: issue.format === "regex"
									? "errors.invalid_format.regex"
									: `errors.invalid_format.${issue.format}`;
				const descriptor = descriptorForKey(key) ?? formValidationMessages.errors.invalid_format.generic;
				return {
					message: render(i18n, descriptor, {
						...baseValues,
						format: validationLabel(i18n, issue.format),
						includes: "includes" in issue ? issue.includes : undefined,
						pattern: "pattern" in issue ? issue.pattern : undefined,
						startsWith: "prefix" in issue ? issue.prefix : undefined,
						endsWith: "suffix" in issue ? issue.suffix : undefined,
					}),
				};
			}
			case "custom": {
				const { descriptor, values, key } = getCustomDescriptorAndValues(
					issueWithMeta.params?.i18n,
					"errors.custom",
				);
				const formattedValues = formatCustomIssueValues({ i18n, key, values });
				return {
					message: render(i18n, descriptor, {
						...formattedValues,
						...baseValues,
						field: formattedValues.field ?? field,
					}),
				};
			}
			case "not_multiple_of":
				return {
					message: render(i18n, formValidationMessages.errors.not_multiple_of, {
						...baseValues,
						divisor: issue.divisor,
					}),
				};
			default:
				return undefined;
		}
	};
